import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faClock,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faBolt,
  faCalendarDay,
  faCalendarAlt,
  faBrain,
  faRoute,
  faChartLine,
  faHome,
  faDollarSign,
  faQuestionCircle,
  faInfoCircle,
  faTasks,
  faArrowRight,
  faPlay,
  faFire,
  faRobot,
  faExternalLinkAlt,
  faLightbulb,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import { formatErrorForReact } from 'helpers/utils';

interface WorkflowStage {
  id: string;
  name: string;
  status: 'completed' | 'active' | 'upcoming';
  daysInStage: number;
  expectedDuration: string;
  completionPercentage: number;
}

interface AIRecommendation {
  id: string;
  title: string;
  description: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  reasoning: string; // Explainable AI - WHY this recommendation
  action: string;
  category: string;
  confidence: number;
  source?: 'database' | 'python_ai' | 'ai_generated';
  // Database-specific fields
  dueDate?: string;
  scheduledTime?: string;
  status?: 'PENDING' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'DECLINED' | 'FAILED';
  isAutomated?: boolean;
  automationResult?: string;
  structuredData?: any;
  assignedTo?: string;
  parentId?: string;
  createdOn?: string;
  updatedOn?: string;
  // Legacy AI fields
  estimatedValue?: number;
  aiType?: 'workflow' | 'consumer_ai' | 'task_routing';
}

interface UpcomingTask {
  id: string;
  title: string;
  description: string;
  dueIn: string;
  priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  assignedTo?: string;
  workflowStage: string;
  aiGenerated: boolean;
}

interface AdvancedInsights {
  confidence_score: number;
  key_insights: string[];
  conversion_probability: number;
  recommended_actions: string[];
}

interface ConsumerAI {
  eligible: boolean;
  recommendations: Array<{
    title: string;
    description: string;
    action_url?: string;
  }>;
  journey_stage: string;
  next_steps: string[];
}

interface EvaluationActivity {
  timestamp: string;
  activity: string;
  status: 'completed' | 'pending' | 'incomplete' | 'skipped';
  result: string;
  details: string;
}

interface EvaluationStatus {
  lastEvaluated: string;
  evaluationScore: number;
  status: 'healthy' | 'attention_needed' | 'critical';
  summary: string;
  activities: EvaluationActivity[];
  issues: string[];
  nextSteps: string[];
  recommendations: {
    immediate: string;
    priority: string;
    timeline: string;
  };
}

interface LeadTask {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  status: string;
  dueDate?: string;
  isCompleted: boolean;
  taskType?: string;
  createdOn?: string;
  aiGenerated: boolean;
  reasoning?: string;
  confidence: number;
  estimatedTime: string;
  category?: string;
}

interface WorkflowProgressData {
  leadId: string;
  leadInfo: {
    name: string;
    email?: string;
    phone?: string;
    source?: string;
    status: string;
    createdAt: string;
  };
  leadClassification: {
    type: 'consumer' | 'professional';
    subtype: string;
    confidence: number;
    potentialValue: number;
  };
  currentWorkflow: {
    instanceId: string;
    template: string;
    type: string;
    description: string;
    stage: {
      name: string;
      description: string;
      progress: number;
      estimatedCompletion: string;
    };
    overallProgress: number;
    status: string;
  } | null;
  aiRecommendations: AIRecommendation[];
  upcomingTasks: Array<{
    id: string;
    title: string;
    dueDate: string;
    priority: 'high' | 'medium' | 'low';
    assignee: string;
  }>;
  leadTasks?: LeadTask[]; // AI-generated tasks for completion tracking
  consumerAI?: ConsumerAI | null;
  advancedInsights?: AdvancedInsights | null;
  contextQuality?: {
    completeness: number;
    lastAnalyzed: string;
    dataPoints: number;
    aiReadiness: boolean;
  };
  evaluationStatus?: EvaluationStatus;
}

interface WorkflowProgressPanelProps {
  leadDetails: any;
  className?: string;
  refreshTrigger?: number;
}

const WorkflowProgressPanel: React.FC<WorkflowProgressPanelProps> = ({
  leadDetails,
  className,
  refreshTrigger
}) => {
  const [workflowData, setWorkflowData] = useState<WorkflowProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [executingAction, setExecutingAction] = useState<string | null>(null);

  useEffect(() => {
    if (leadDetails?.id) {
      loadWorkflowProgress();
    }
  }, [leadDetails?.id, refreshTrigger]);

  const loadWorkflowProgress = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Call the new Workflow Orchestrator endpoint
      console.log(`🔄 [FRONTEND] Calling workflow progress endpoint: /workflow/lead-progress/${leadDetails.id}`);
      const response = await axiosInstance.get(`/workflow/lead-progress/${leadDetails.id}`);
      
      if (response.data.success && response.data.data) {
        // Transform backend data to frontend format
        const backendData = response.data.data;
        
        // Debug logging to see what we're receiving
        console.log('🔍 [DEBUG] Backend data received:', {
          leadClassification: backendData.leadClassification,
          currentWorkflow: backendData.currentWorkflow,
          aiRecommendations: backendData.aiRecommendations?.length || 0,
          upcomingTasks: backendData.upcomingTasks?.length || 0
        });
        
        const workflowProgress: WorkflowProgressData = {
          leadId: backendData.leadId,
          leadInfo: backendData.leadInfo,
          leadClassification: {
            type: backendData.leadClassification.type || 'consumer',
            subtype: backendData.leadClassification.subtype || backendData.leadClassification.category || 'Unclassified Lead',
            confidence: backendData.leadClassification.confidence || 0,
            potentialValue: backendData.leadClassification.potentialValue || 0
          },
          currentWorkflow: backendData.currentWorkflow ? {
            instanceId: backendData.currentWorkflow.instanceId,
            template: backendData.currentWorkflow.template || 'Discovery Workflow',
            type: backendData.currentWorkflow.type,
            description: backendData.currentWorkflow.description,
            stage: {
              name: backendData.currentWorkflow.stage || 'Initial Contact',
              description: backendData.currentWorkflow.description || 'Getting started with lead qualification',
              progress: backendData.currentWorkflow.overallProgress || 10,
              estimatedCompletion: '3-5 days'
            },
            overallProgress: backendData.currentWorkflow.overallProgress || 10,
            status: backendData.currentWorkflow.status || 'ACTIVE'
          } : null,
          aiRecommendations: backendData.aiRecommendations || [],
          upcomingTasks: backendData.upcomingTasks || [],
          consumerAI: backendData.consumerAI,
          advancedInsights: backendData.advancedInsights,
          contextQuality: backendData.contextQuality,
          evaluationStatus: backendData.evaluationStatus
                 };
         
         setWorkflowData(workflowProgress);
      } else {
        // No workflow data found - lead needs workflow assignment
        setWorkflowData(null);
      }
    } catch (error: any) {
      console.error('❌ [FRONTEND] Error loading workflow progress:', error);
      console.error('❌ [FRONTEND] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        code: error.code,
        message: error.message,
        url: error.config?.url
      });
      
      // Check if this is a 404 (endpoint doesn't exist) vs other errors
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        // API endpoint not implemented yet - show empty state instead of error
        console.log('Workflow progress endpoint not implemented yet, showing empty state');
        setWorkflowData(null);
        setError('');
      } else {
        // Real error - show error state
        const errorMessage = error.response?.data?.message || 'Unable to load workflow progress. Please try again.';
        setError(errorMessage);
        setWorkflowData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkflow = async () => {
    try {
      setExecutingAction('auto_assign');
      
      // Step 1: Call Workflow Orchestrator to initialize lead workflow
      const response = await axiosInstance.post('/workflow/initialize-lead', {
        leadId: leadDetails.id,
        leadData: {
          lead_first_name: leadDetails.lead_first_name,
          lead_last_name: leadDetails.lead_last_name,
          lead_email: leadDetails.lead_email,
          lead_phone: leadDetails.lead_phone,
          // Add other lead context as needed
        },
        trigger: 'manual_assignment',
        source: 'lead_details_page'
      });

      if (response.data.success) {
        toast.success(`Workflow assigned successfully! ${leadDetails.lead_first_name} is now in the ${response.data.workflowType} workflow.`);
        
        // Refresh workflow progress to show the newly assigned workflow
        await loadWorkflowProgress();
      } else {
        throw new Error(response.data.message || 'Failed to assign workflow');
      }
    } catch (error: any) {
      console.error('Error initializing workflow:', error);
      
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        toast.error('Workflow orchestrator endpoint not yet implemented. This feature will be available when the backend API is ready.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to assign workflow';
        toast.error(`Workflow assignment failed: ${errorMessage}`);
      }
    } finally {
      setExecutingAction(null);
    }
  };

  const executeRecommendation = async (recommendation: any) => {
    try {
      setExecutingAction(recommendation.id);
      
      // If this is a database recommendation, mark it as completed
      if (recommendation.source === 'database') {
        const response = await fetch(`/api/workflow/recommendations/${recommendation.id}/complete`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-User-ID': localStorage.getItem('userId') || '',
            'X-User-JWT': localStorage.getItem('token') || ''
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to mark recommendation as complete');
        }
        
        const result = await response.json();
        console.log('✅ Recommendation marked as complete:', result);
        
        // Show success message
        // toast.success(`${recommendation.title} marked as completed`);
        
        // Refresh workflow data to show updated status
        await loadWorkflowProgress();
        
      } else if (recommendation.category === 'consumer_ai' || recommendation.action.includes('ai')) {
        // Route to Consumer AI
        const aiRoute = recommendation.action.replace('route_to_', '').replace('_ai', '');
        window.open(`/consumer-ai/${aiRoute}?lead_id=${leadDetails.id}`, '_blank');
      } else {
        // Execute workflow action
        await axiosInstance.post(`/workflow/execute-recommendation`, {
          leadId: leadDetails.id,
          recommendationId: recommendation.id,
          action: recommendation.action
        });
        // toast.success(`${recommendation.title} executed successfully`);
        loadWorkflowProgress(); // Refresh data
      }
    } catch (error: any) {
      console.error('Failed to execute recommendation:', error);
      // toast.error(`Failed to execute: ${error.response?.data?.message || 'Unknown error'}`);
    } finally {
      setExecutingAction(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'URGENT': return faFire;
      case 'HIGH': return faExclamationTriangle;
      case 'MEDIUM': return faInfoCircle;
      case 'LOW': return faQuestionCircle;
      default: return faInfoCircle;
    }
  };

  const handleMarkTaskComplete = async (taskId: string, taskTitle: string) => {
    try {
      console.log(`Marking task ${taskId} as complete: ${taskTitle}`);
      
      // Call the API to mark task as complete
      const response = await fetch(`/api/workflow/tasks/${taskId}/complete`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-User-ID': localStorage.getItem('userId') || '',
          'X-User-JWT': localStorage.getItem('token') || ''
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark task as complete');
      }
      
      const result = await response.json();
      
      // Check if workflow progression was triggered
      if (result.data?.workflowProgression) {
        const progression = result.data.workflowProgression;
        if (progression.progressed) {
          console.log(`🚀 Workflow progressed to: ${progression.nextStage?.stageName}`);
          // Show success notification about workflow progression
          // toast.success(`🚀 Workflow advanced to ${progression.nextStage?.stageName}!`);
        }
        
        if (result.data?.newTasksGenerated) {
          console.log(`🎯 New tasks generated for next stage`);
          // toast.info(`✨ New tasks available for ${progression.nextStage?.stageName} stage`);
        }
      }
      
      // Refresh workflow data to show updated task status and potential new stage
      await loadWorkflowProgress();
      
    } catch (error) {
      console.error('Failed to mark task as complete:', error);
      // You might want to show a user-friendly error message here
    }
  };

  if (loading) {
    return (
      <Card className={classNames(className, "h-100")}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading workflow progress...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error && !workflowData) {
    return (
      <Card className={classNames(className, "h-100")}>
        <Card.Header className="bg-light border-bottom">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
            <h6 className="mb-0 fw-bold">Workflow Progress</h6>
          </div>
        </Card.Header>
        <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center p-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning mb-3" size="3x" />
          <h6 className="text-muted mb-2">Unable to Load Workflow Data</h6>
          <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
            {formatErrorForReact(error)}
          </p>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadWorkflowProgress}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Retrying...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRoute} className="me-2" />
                Retry Loading
              </>
            )}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // Handle empty data state - trigger automatic workflow assignment
  if (!workflowData && !loading && !error) {
    return (
      <Card className={classNames(className, "h-100")}>
        <Card.Header className="bg-light border-bottom">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faRocket} className="text-primary me-2" />
            <h6 className="mb-0 fw-bold">Workflow Assignment</h6>
          </div>
        </Card.Header>
        <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center p-4">
          <FontAwesomeIcon icon={faBolt} className="text-warning mb-3" size="3x" />
          <h6 className="text-dark mb-2">Ready for Workflow Assignment</h6>
          <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
            {leadDetails.lead_first_name} needs to be assigned to a workflow for intelligent lead management.
          </p>
          
          <div className="d-flex gap-2 mb-3">
            <Button
              variant="primary"
              size="sm"
              onClick={initializeWorkflow}
              disabled={executingAction === 'auto_assign'}
            >
              {executingAction === 'auto_assign' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Assigning Workflow...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faBolt} className="me-2" />
                  Auto-Assign Workflow
                </>
              )}
            </Button>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadWorkflowProgress}
              disabled={executingAction === 'auto_assign'}
            >
              <FontAwesomeIcon icon={faRoute} className="me-2" />
              Refresh
            </Button>
          </div>

          <div className="text-start w-100">
            <small className="text-muted fw-medium">🧠 Workflow Orchestrator will:</small>
            <ul className="text-muted mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
              <li>Classify: Professional vs Consumer contact</li>
              <li>Select: Buyer/Seller/Professional workflow</li>
              <li>Initialize: Stage progression & tasks</li>
              <li>Enable: AI recommendations & routing</li>
            </ul>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!workflowData) return null;

  return (
    <Card className={classNames(className, "h-100 workflow-progress-panel")}>
      <Card.Header className="bg-light border-bottom">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faRocket} className="text-primary me-2" />
            <div>
              <h6 className="mb-0 fw-bold">Workflow Progress</h6>
              <small className="text-muted">{workflowData.currentWorkflow?.template}</small>
            </div>
          </div>
          <Badge bg="success" className="fw-bold">
            {workflowData.currentWorkflow?.overallProgress}%
          </Badge>
        </div>
      </Card.Header>

      <Card.Body className="p-3">
        {/* Lead Classification */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-medium text-dark">Lead Classification</span>
            {workflowData.leadClassification.confidence > 0 ? (
              <Badge bg="info" className="fw-bold">
                {workflowData.leadClassification.confidence}% confidence
              </Badge>
            ) : (
              <Badge bg="secondary" className="fw-bold">
                Analyzing...
              </Badge>
            )}
          </div>
          <div className="bg-light rounded p-2">
            <div className="fw-semibold text-primary">
              {workflowData.leadClassification.subtype}
            </div>
            {workflowData.leadClassification.potentialValue > 0 ? (
              <small className="text-muted">
                Potential Value: ${workflowData.leadClassification.potentialValue.toLocaleString()}
              </small>
            ) : (
              <small className="text-muted">
                Classification: {workflowData.leadClassification.type} lead
              </small>
            )}
          </div>
        </div>

        {/* Current Stage Progress */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-medium text-dark">Current Stage</span>
            <small className="text-muted">
              Day {workflowData.currentWorkflow?.stage.progress} of {workflowData.currentWorkflow?.stage.estimatedCompletion}
            </small>
          </div>
          <div className="bg-primary bg-opacity-10 rounded p-3">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon 
                icon={workflowData.currentWorkflow?.status === 'ACTIVE' ? faPlay : 
                     workflowData.currentWorkflow?.overallProgress === 100 ? faCheckCircle : faClock} 
                className={`me-2 ${workflowData.currentWorkflow?.status === 'ACTIVE' ? 'text-success' : 'text-primary'}`}
              />
              <span className="fw-semibold">{workflowData.currentWorkflow?.stage.name}</span>
            </div>
            <ProgressBar 
              now={workflowData.currentWorkflow?.overallProgress || 10} 
              className="mb-2"
              style={{ height: '6px' }}
            />
            <small className="text-muted">
              {workflowData.currentWorkflow?.overallProgress || 10}% complete
            </small>
          </div>
        </div>

        {/* Evaluation Status & Activity Log */}
        {workflowData.evaluationStatus && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faChartLine} className="text-info me-2" />
                <span className="fw-medium text-dark">Evaluation Status</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge 
                  bg={workflowData.evaluationStatus.status === 'healthy' ? 'success' : 
                      workflowData.evaluationStatus.status === 'attention_needed' ? 'warning' : 'danger'}
                  className="fw-bold"
                >
                  {workflowData.evaluationStatus.evaluationScore}%
                </Badge>
                <Badge 
                  bg={workflowData.evaluationStatus.status === 'healthy' ? 'success' : 
                      workflowData.evaluationStatus.status === 'attention_needed' ? 'warning' : 'danger'}
                  className="text-capitalize"
                >
                  {workflowData.evaluationStatus.status.replace('_', ' ')}
                </Badge>
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-light rounded p-3 mb-3">
              <div className="fw-semibold text-primary mb-2">
                {workflowData.evaluationStatus.summary}
              </div>
              <small className="text-muted">
                Last evaluated: {new Date(workflowData.evaluationStatus.lastEvaluated).toLocaleString()}
              </small>
            </div>

            {/* Issues (if any) */}
            {workflowData.evaluationStatus.issues.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                  <span className="fw-semibold text-warning">Issues Identified</span>
                </div>
                {workflowData.evaluationStatus.issues.map((issue, index) => (
                  <div key={index} className="bg-warning bg-opacity-10 border-start border-warning border-3 p-2 mb-1">
                    <small className="text-dark">{issue}</small>
                  </div>
                ))}
              </div>
            )}

            {/* Next Steps */}
            {workflowData.evaluationStatus.nextSteps.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faArrowRight} className="text-primary me-2" />
                  <span className="fw-semibold text-primary">Recommended Next Steps</span>
                </div>
                {workflowData.evaluationStatus.nextSteps.map((step, index) => (
                  <div key={index} className="d-flex align-items-start p-2 border rounded mb-1">
                    <Badge bg="primary" className="me-2 mt-1" style={{ fontSize: '0.6rem' }}>
                      {index + 1}
                    </Badge>
                    <small className="text-dark flex-grow-1">{step}</small>
                  </div>
                ))}
              </div>
            )}

            {/* Recent Activities */}
            {workflowData.evaluationStatus.activities.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faClock} className="text-secondary me-2" />
                  <span className="fw-semibold text-secondary">Recent Evaluation Activities</span>
                </div>
                <div className="max-height-150 overflow-auto">
                  {workflowData.evaluationStatus.activities.map((activity, index) => (
                    <div key={index} className="d-flex align-items-start p-2 border-bottom">
                      <FontAwesomeIcon 
                        icon={activity.status === 'completed' ? faCheckCircle : 
                              activity.status === 'pending' ? faClock : 
                              activity.status === 'incomplete' ? faExclamationTriangle : 
                              faQuestionCircle} 
                        className={`me-2 mt-1 ${
                          activity.status === 'completed' ? 'text-success' : 
                          activity.status === 'pending' ? 'text-warning' : 
                          activity.status === 'incomplete' ? 'text-danger' : 
                          'text-secondary'
                        }`}
                        size="sm"
                      />
                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>
                          {activity.activity}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {activity.result}
                        </div>
                        {activity.details && (
                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {activity.details}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Priority Recommendation */}
            <div className="bg-primary bg-opacity-10 rounded p-3">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faLightbulb} className="text-primary me-2" />
                <span className="fw-semibold text-primary">Priority Action</span>
                <Badge bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>
                  {workflowData.evaluationStatus.recommendations.timeline}
                </Badge>
              </div>
              <div className="fw-semibold mb-1" style={{ fontSize: '0.9rem' }}>
                {workflowData.evaluationStatus.recommendations.priority}
              </div>
              <small className="text-muted">
                {workflowData.evaluationStatus.recommendations.immediate}
              </small>
            </div>
          </div>
        )}

        {/* AI Recommendations */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faBrain} className="text-warning me-2" />
            <span className="fw-medium text-dark">AI Recommendations</span>
            <Badge bg="warning" className="ms-2 fw-bold">
              {workflowData.aiRecommendations?.length || 0}
            </Badge>
          </div>
          
          {workflowData.aiRecommendations && workflowData.aiRecommendations.length > 0 ? (
            workflowData.aiRecommendations.map((rec) => (
              <div key={rec.id} className="border rounded p-2 mb-2">
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <FontAwesomeIcon 
                        icon={getPriorityIcon(rec.priority)} 
                        className={`text-${getPriorityColor(rec.priority)} me-2`}
                      />
                      <span className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                        {rec.title}
                      </span>
                      <Badge bg={getPriorityColor(rec.priority)} className="ms-2" style={{ fontSize: '0.7rem' }}>
                        {rec.priority}
                      </Badge>
                      {/* Source indicator */}
                      {rec.source && (
                        <Badge 
                          bg={rec.source === 'database' ? 'info' : rec.source === 'python_ai' ? 'success' : 'secondary'} 
                          className="ms-1" 
                          style={{ fontSize: '0.6rem' }}
                        >
                          {rec.source === 'database' ? 'DB' : rec.source === 'python_ai' ? 'AI+' : 'AI'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                      {rec.description}
                    </p>
                    
                    {/* Database-specific fields */}
                    {rec.source === 'database' && (
                      <div className="mb-2">
                        <div className="d-flex flex-wrap gap-2 mb-1">
                          {rec.status && (
                            <Badge 
                              bg={rec.status === 'PENDING' ? 'warning' : rec.status === 'IN_PROGRESS' ? 'primary' : 'success'} 
                              style={{ fontSize: '0.6rem' }}
                            >
                              {rec.status}
                            </Badge>
                          )}
                          {rec.dueDate && (
                            <small className="text-muted">
                              <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                              Due: {new Date(rec.dueDate).toLocaleDateString()}
                            </small>
                          )}
                          {rec.isAutomated && (
                            <Badge bg="info" style={{ fontSize: '0.6rem' }}>
                              <FontAwesomeIcon icon={faRobot} className="me-1" />
                              Automated
                            </Badge>
                          )}
                        </div>
                        {rec.assignedTo && (
                          <small className="text-muted d-block">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            Assigned to: {rec.assignedTo}
                          </small>
                        )}
                      </div>
                    )}
                    
                    {/* Explainable AI */}
                    <div className="bg-light rounded p-2 mb-2">
                      <div className="d-flex align-items-start">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-info me-2 mt-1" size="sm" />
                        <div>
                          <small className="fw-medium text-info">
                            {rec.source === 'database' ? 'Database Recommendation:' : 'AI Reasoning:'}
                          </small>
                          <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                            {rec.reasoning}
                          </small>
                          <small className="text-info fw-medium" style={{ fontSize: '0.75rem' }}>
                            Confidence: {Math.round(rec.confidence || 0)}%
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={rec.priority === 'high' || rec.priority === 'urgent' ? 'danger' : 'primary'}
                  className="w-100"
                  onClick={() => executeRecommendation(rec)}
                  disabled={executingAction === rec.id || rec.status === 'IN_PROGRESS'}
                >
                  {executingAction === rec.id ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Executing...
                    </>
                  ) : rec.status === 'IN_PROGRESS' ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                      In Progress
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlay} className="me-2" />
                      {rec.category === 'consumer_ai' ? 'Launch AI Tool' : 'Execute Action'}
                    </>
                  )}
                </Button>
              </div>
            ))
          ) : (
            <div className="text-center p-3 text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="mb-2" size="2x" />
              <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                No AI recommendations available at this time.
              </p>
            </div>
          )}
        </div>

        {/* AI-Generated Tasks for Tracking */}
        {workflowData.leadTasks && workflowData.leadTasks.length > 0 && (
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon icon={faTasks} className="text-primary me-2" />
              <span className="fw-medium text-dark">AI Tasks</span>
              <Badge bg="primary" className="ms-2 fw-bold">
                {workflowData.leadTasks.filter(task => !task.isCompleted).length}
              </Badge>
            </div>
            
            {workflowData.leadTasks.map((task) => (
              <div key={task.id} className="border rounded p-3 mb-3 bg-light">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center">
                    <input 
                      type="checkbox" 
                      className="form-check-input me-2"
                      checked={task.isCompleted}
                      onChange={() => handleMarkTaskComplete(task.id, task.title)}
                    />
                    <h6 className={`mb-1 fw-bold ${task.isCompleted ? 'text-muted text-decoration-line-through' : 'text-dark'}`}>
                      {task.title}
                      {task.isCompleted && (
                        <span className="ms-2 text-success fw-normal" style={{ fontSize: '0.8rem' }}>
                          - Completed
                        </span>
                      )}
                    </h6>
                  </div>
                  <div className="d-flex gap-2">
                    <Badge 
                      bg={task.priority === 'high' ? 'danger' : 
                         task.priority === 'medium' ? 'warning' : 'secondary'}
                      className="text-white"
                    >
                      {task.priority?.toUpperCase()}
                    </Badge>
                    <Badge 
                      bg={task.isCompleted ? 'success' : 'info'}
                      className="text-white"
                    >
                      {task.status}
                    </Badge>
                  </div>
                </div>
                
                <p className="text-muted mb-2 small">{task.description}</p>
                
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-3">
                    {task.dueDate && (
                      <small className="text-muted">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Due: {new Date(task.dueDate).toLocaleDateString()}
                      </small>
                    )}
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faBrain} className="me-1" />
                      AI: {task.confidence}%
                    </small>
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {task.estimatedTime}
                    </small>
                  </div>
                </div>
                
                {task.reasoning && (
                  <div className="mt-2 p-2 bg-white rounded border-start border-primary border-3">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faLightbulb} className="text-primary me-1" />
                      <strong>AI Reasoning:</strong> {task.reasoning}
                    </small>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Enhanced Consumer AI Section */}
        {workflowData?.consumerAI && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-bold text-primary">
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                Consumer AI Recommendations
              </h6>
              {workflowData.consumerAI.eligible && (
                <Badge bg="success" className="px-2 py-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                  AI Ready
                </Badge>
              )}
            </div>
            
            {workflowData.consumerAI.eligible ? (
              <>
                <div className="mb-3">
                  <small className="text-muted">Journey Stage: </small>
                  <Badge bg="info" className="ms-1">
                    {workflowData.consumerAI.journey_stage}
                  </Badge>
                </div>
                
                {workflowData.consumerAI.recommendations.length > 0 && (
                  <div className="mb-3">
                    <small className="fw-bold text-secondary mb-2 d-block">AI Recommendations:</small>
                    {workflowData.consumerAI.recommendations.map((rec, index) => (
                      <div key={index} className="border rounded p-2 mb-2 bg-light">
                        <div className="fw-bold text-sm">{rec.title}</div>
                        <div className="text-muted small">{rec.description}</div>
                        {rec.action_url && (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="mt-2"
                            onClick={() => window.open(rec.action_url, '_blank')}
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                            Access AI Tool
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {workflowData.consumerAI.next_steps.length > 0 && (
                  <div className="mb-3">
                    <small className="fw-bold text-secondary mb-2 d-block">Next Steps:</small>
                    <ul className="list-unstyled mb-0">
                      {workflowData.consumerAI.next_steps.map((step, index) => (
                        <li key={index} className="text-sm mb-1">
                          <FontAwesomeIcon icon={faArrowRight} className="me-2 text-primary" />
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-3">
                <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                <p className="text-muted mb-0">
                  More lead data needed for AI recommendations
                </p>
              </div>
            )}
          </div>
        )}

        {/* Advanced AI Insights Section */}
        {workflowData?.advancedInsights && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-bold text-success">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Advanced AI Insights
              </h6>
              <Badge 
                bg={workflowData.advancedInsights.confidence_score > 0.8 ? 'success' : 
                   workflowData.advancedInsights.confidence_score > 0.6 ? 'warning' : 'secondary'}
                className="px-2 py-1"
              >
                {Math.round(workflowData.advancedInsights.confidence_score * 100)}% Confidence
              </Badge>
            </div>
            
            {/* Conversion Probability */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="fw-bold text-secondary">Conversion Probability</small>
                <small className="text-success fw-bold">
                  {Math.round(workflowData.advancedInsights.conversion_probability * 100)}%
                </small>
              </div>
              <ProgressBar 
                now={workflowData.advancedInsights.conversion_probability * 100}
                variant={workflowData.advancedInsights.conversion_probability > 0.7 ? 'success' : 
                        workflowData.advancedInsights.conversion_probability > 0.4 ? 'warning' : 'danger'}
                style={{ height: '8px' }}
              />
            </div>
            
            {/* Key Insights */}
            {workflowData.advancedInsights.key_insights.length > 0 && (
              <div className="mb-3">
                <small className="fw-bold text-secondary mb-2 d-block">Key Insights:</small>
                <ul className="list-unstyled mb-0">
                  {workflowData.advancedInsights.key_insights.map((insight, index) => (
                    <li key={index} className="text-sm mb-2 border-left pl-3">
                      <FontAwesomeIcon icon={faLightbulb} className="me-2 text-warning" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Recommended Actions */}
            {workflowData.advancedInsights.recommended_actions.length > 0 && (
              <div className="mb-3">
                <small className="fw-bold text-secondary mb-2 d-block">Claude 4 Recommendations:</small>
                {workflowData.advancedInsights.recommended_actions.map((action, index) => (
                  <div key={index} className="border rounded p-2 mb-2 bg-light">
                    <div className="text-sm">
                      <FontAwesomeIcon icon={faRobot} className="me-2 text-primary" />
                      {action}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Context Quality Indicator */}
        {workflowData?.contextQuality && (
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <small className="fw-bold text-secondary">Data Quality</small>
              <Badge 
                bg={workflowData.contextQuality.aiReadiness ? 'success' : 'warning'}
                className="px-2 py-1"
              >
                {workflowData.contextQuality.aiReadiness ? 'AI Ready' : 'Building Context'}
              </Badge>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted">Completeness</small>
              <small className="text-primary fw-bold">
                {Math.round(workflowData.contextQuality.completeness * 100)}%
              </small>
            </div>
            <ProgressBar 
              now={workflowData.contextQuality.completeness * 100}
              variant={workflowData.contextQuality.completeness > 0.8 ? 'success' : 
                      workflowData.contextQuality.completeness > 0.5 ? 'warning' : 'danger'}
              style={{ height: '6px' }}
            />
            <div className="mt-2">
              <small className="text-muted">
                Data Points: {workflowData.contextQuality.dataPoints} | 
                Last Updated: {new Date(workflowData.contextQuality.lastAnalyzed).toLocaleDateString()}
              </small>
            </div>
          </div>
        )}


      </Card.Body>
    </Card>
  );
};

export default WorkflowProgressPanel; 